import {useEffect, useRef} from 'react';
import type {Closure, ServiceStatus} from '../../api';
import {useRouter} from '../../app/router';
import {ROUTES} from '../../app/routes';
import {useI18n} from '../../i18n';
import {useMenu} from '../../menu';
import {useGuestSession, useServiceStatus} from '../../session';
import {ActionLink, Card, Screen, ScreenBody, ScreenFooter, TerminalNotice} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';
import './q20.css';

/**
 * # Q20 · Venue closed / not accepting orders
 *
 * Route `/closed`. Reached from Q1 and Q12 when the server itself says the
 * location is not serving, and by a guest who reloads or bookmarks the page.
 *
 * ## This is not an error, and it is not a waiting room
 *
 * A closed restaurant is a fact about the day. Nothing here uses the error
 * palette, nothing apologises for the system, and there is no diagnostic. The
 * ring is Palm Sugar — this surface's "needs your attention and is not an
 * error" — and never Dried Chili, which Q19 keeps for a credential that is
 * genuinely dead.
 *
 * There is also **no retry control and no polling loop**, and that is a decision
 * rather than an omission: 03 §Q9 records "venue-paused is an honest dead end —
 * no retry spinner, no queue, no 'try again shortly' that resolves into nothing.
 * A hopeful dead end is worse than a clear one — it keeps a guest tapping
 * instead of waving." `useServiceStatus().reload` therefore exists and is
 * deliberately never called from this screen. The links that *are* rendered all
 * resolve into something real: an order that exists, a menu that loaded, and a
 * language switch.
 *
 * ## Unknown is not closed
 *
 * `verdict === null` means the server declined the question — `getServiceStatus`
 * answers `501` on the contract mock, and its `security` block does not list the
 * guest credential even though its description says guest phones may read it
 * (flagged in `api/client.ts`, not patched — ADR-0005). A closed notice shown in
 * an open restaurant loses the guest permanently, which is a far worse failure
 * than a menu shown five minutes early. So this screen renders a closed notice
 * **only** from an explicit `closed` / `outside_hours` verdict, and a guest who
 * arrives here while the server says `open` — or says nothing at all — is sent
 * on to the menu instead of being shown a shut door this client invented.
 *
 * ## Three sentences, and which guest gets which
 *
 * `closed` and `outside_hours` are different facts and get different titles
 * (`q20.closedTitle` vs `q20.outsideHoursTitle`). The third case is the one the
 * contract makes possible and the state enum does not name:
 *
 * > "**A closure does not stop staff from selling.** It suppresses the public
 * > ordering surfaces; the till stays live. A shop shut to the public is
 * > routinely still cashing out a table that was seated before the gas ran out."
 * > — `declareClosure`, `pokpok-api.v1.yaml`
 *
 * A guest whose table already has an open order (`session.active_order_id`) is
 * exactly that table. Telling them "the restaurant is closed" while a server is
 * carrying their food across the room is false and frightening; what actually
 * happened is that **QR ordering** stopped, and staff can still take the order.
 * That guest gets `q20.notAcceptingTitle` + `q20.notAcceptingBody` and a link to
 * the food they are already waiting for. Flagged in the report as a design
 * judgment, because no field in the frozen contract says "QR ordering is paused"
 * and this is the nearest honest reading of one that does exist.
 *
 * ## What is deliberately not shown
 *
 * **The week's opening hours.** 03 §Q20 lists hours as data, and `q20.hoursHeading`
 * / `q20.hoursToday` / `q20.hoursUnknown` / `q20.day.*` exist for them — but
 * `ServiceStatus` carries no hours (only `hours_configured`, a boolean), and the
 * one operation that does carry them, `GET /v1/location/opening-hours`, is not on
 * the `ApiClient` the spine built. Rather than invent a week or compose one on a
 * device that has neither the merchant's timezone nor the trading-day cutover,
 * this screen shows nothing about hours and the gap is reported.
 *
 * **A reopening time further out than a day.** `q20.reopensAt` interpolates a
 * wall clock, which only parses as an answer if it means *today*: "เปิดอีกครั้ง
 * 18:00" against a closure that ends on Thursday would send a guest back tonight.
 * Beyond the horizon the time is dropped and the merchant's own message — which
 * is where a date actually lives ("ปิดสงกรานต์ กลับมาเปิด 16 เมษายน" is the
 * contract's own example) — carries the answer.
 *
 * **A composed opening time.** Never computed from the device. The timezone and
 * the trading-day cutover belong to the server, and this page only ever formats
 * an instant the server sent.
 *
 * ## Money, photographs, and the 86 rule
 *
 * No money is displayed, so nothing here can be Pandan — neither this file nor
 * `q20.css` references `--pokpok-color-money-verified`, so D-021 cannot be broken
 * here even by accident. No image is requested either: the mark inside the ring
 * is one character in `currentColor`, which is the no-photo case rendered as the
 * only case.
 *
 * This screen lists no menu items, so an item vanishing from the projection has
 * nothing to hide behind here. What it owes the one-menu promise is the other
 * direction: it does not offer "you can still look at the menu" unless a menu
 * with items in it actually exists on this device, because a link to an empty
 * menu is precisely the hopeful dead end §Q9 rules out.
 *
 * ## REDESIGN · the shared terminal shell, and the one red this screen may spend
 *
 * The ring, the heading, the card and the ways out are `TerminalNotice`, `Card`
 * and `ActionLink` now; the hand-drawn shutter, the hand-sized heading and the
 * hand-rolled outline link are gone.
 *
 * The old stylesheet forbade `--color-accent` outright, on the grounds that a
 * solid red means "primary action" and this screen has none, because the thing
 * the guest came to do cannot be done. That is exactly right for the two closed
 * flavours and it is **not** right for the third: a guest mid-meal, whose food is
 * already in the kitchen, has a real forward action, and it is the only thing on
 * the page that is genuinely theirs to do. So `notAccepting` — and only
 * `notAccepting` — spends the screen's one red on the order status. `closed` and
 * `outsideHours` still carry no solid action at all, because for them there is
 * still nothing forward to go to.
 */

/** Which of the three sentences a guest reads, or `waiting` while nothing is known yet. */
type Q20Flavour = 'closed' | 'outsideHours' | 'notAccepting' | 'waiting';

/**
 * How far ahead a bare wall clock still reads as an answer.
 *
 * A day. `q20.reopensAt` has one `{time}` slot and no date, so anything beyond
 * roughly today is shown as no time at all rather than as a time that means a
 * different day. There is no key on this surface that can say "reopens on the
 * 16th"; that gap is in the report.
 */
const REOPEN_HORIZON_MS = 24 * 60 * 60 * 1000;

/**
 * The instant the venue reopens, if the server named one that a clock can carry.
 *
 * Two field names are read because the transcribed `Closure` type and the frozen
 * YAML disagree: `types.ts` declares `ends_at`, the contract declares
 * `closed_until` (and `Closure` has an index signature, so the contract's name is
 * reachable). `types.ts` says in its own header that where the two disagree the
 * YAML wins — so the contract's field is preferred and the transcription is the
 * fallback. Reported, not patched: `api/types.ts` belongs to the spine.
 *
 * Everything here is instant arithmetic against the **server's** `as_of`, never
 * the device clock, so a phone with the wrong date cannot suppress or invent a
 * reopening time.
 */
function reopenInstant(verdict: ServiceStatus | null): string | null {
  const closure = verdict?.closure;
  if (!closure || !verdict) return null;

  const fromContract = closure.closed_until;
  const candidate =
    typeof fromContract === 'string' && fromContract.trim() !== ''
      ? fromContract
      : (closure.ends_at ?? null);
  if (!candidate) return null;

  const at = Date.parse(candidate);
  if (Number.isNaN(at)) return null;

  const asOf = Date.parse(verdict.as_of);
  const base = Number.isNaN(asOf) ? Date.now() : asOf;

  // Already past, or too far out for a bare wall clock to mean anything.
  if (at <= base || at - base > REOPEN_HORIZON_MS) return null;
  return candidate;
}

/**
 * The merchant's own guest-facing words, shown as written.
 *
 * The contract carries `message_th` and **deliberately no `message_en` or
 * `message_zh_hans`** — whether the guest surface translates a merchant-typed
 * notice is an open product question, and it says so in as many words. So this
 * text stays Thai and is marked `lang="th"`, which is what makes a browser set it
 * in a Thai face rather than a Chinese one for a guest reading the rest of the
 * page in 中文. It is never machine-translated and never paraphrased.
 *
 * The transcribed type's `message` (a `LocalizedText`) is accepted as a fallback
 * in case the field lands later; the mismatch is in the report.
 */
function closureNotice(
  closure: Closure | null | undefined,
  ln: (text: {th: string; en?: string | null; zh_hans?: string | null}) => string,
): {text: string; lang?: string} | null {
  if (!closure) return null;

  const thai = closure.message_th;
  if (typeof thai === 'string' && thai.trim() !== '') {
    return {text: thai.trim(), lang: 'th'};
  }

  const localised = closure.message;
  if (localised && typeof localised.th === 'string') {
    const text = ln(localised).trim();
    if (text !== '') return {text};
  }
  return null;
}

export default function Q20VenueClosed() {
  const {t, ln, time} = useI18n();
  const {navigate} = useRouter();
  const {status: sessionStatus, session} = useGuestSession();
  const {verdict, loading} = useServiceStatus();
  const {itemsById} = useMenu();

  // Whether the service-status read has been attempted at all. Without it, the
  // first render — where nothing has loaded and `verdict` is null purely because
  // the request has not started — is indistinguishable from "the server refused
  // to answer", and the redirect below would fire before the question was asked.
  const attempted = useRef(false);
  useEffect(() => {
    if (loading) attempted.current = true;
  }, [loading]);

  // No usable credential: this page cannot say anything true about a venue it
  // cannot ask about. Q19 owns all four of those sentences — nothing scanned, a
  // dead QR, a regenerated one, a venue whose API is not answering — so the guest
  // goes there rather than reading a closed notice that was never verified.
  useEffect(() => {
    if (sessionStatus === 'active' || sessionStatus === 'opening') return;
    navigate(ROUTES.invalid, {replace: true});
  }, [sessionStatus, navigate]);

  const state = verdict?.state ?? null;
  const notServing = state === 'closed' || state === 'outside_hours';

  // The venue is serving, or the server would not say. Either way this screen has
  // nothing true to tell them, and the menu is where they were going.
  useEffect(() => {
    if (sessionStatus !== 'active' || loading || notServing) return;
    if (state === 'open' || (verdict === null && attempted.current)) {
      navigate(ROUTES.menu, {replace: true});
    }
  }, [sessionStatus, loading, notServing, state, verdict, navigate]);

  // An open order at this table means service is under way — a closure has
  // suppressed the QR channel, not the restaurant. See the note above.
  const midService = Boolean(session?.active_order_id);
  const flavour: Q20Flavour = !notServing
    ? 'waiting'
    : midService
      ? 'notAccepting'
      : state === 'closed'
        ? 'closed'
        : 'outsideHours';

  const notice = closureNotice(verdict?.closure, ln);
  const reopen = reopenInstant(verdict);
  const venueName = session?.merchant_display_name ? ln(session.merchant_display_name) : '';
  // A menu that exists on this device, not a menu we hope will load.
  const menuBrowsable = itemsById.size > 0;

  if (flavour === 'waiting') {
    // One quiet line, and no spinner: on the two paths out of this state the
    // page is about to be replaced by a notice or by the menu, and a spinner
    // would be the "try again shortly" that §Q9 rules out.
    return (
      <Screen className="q20-screen">
        <ScreenBody center>
          <p className="pk-muted" aria-live="polite">
            {t('common.loading')}
          </p>
        </ScreenBody>
      </Screen>
    );
  }

  return (
    <Screen className="q20-screen">
      <ScreenBody center>
        {/*
          `aria-live="polite"`, not `role="alert"`. The guest has landed on this
          page; they are not being interrupted mid-task, and an assertive alert
          would talk over a screen reader in the middle of a sentence.
        */}
        <div className="q20-live" aria-live="polite">
          {/* The venue leads (03 §Q1's recommendation), even on the day it is
              shut — and above the notice, because whose door this is comes
              before what the door is doing. */}
          {venueName ? <p className="pk-muted q20-venue">{venueName}</p> : null}

          <TerminalNotice
            tone="warning"
            glyph="!"
            title={t(
              flavour === 'notAccepting'
                ? 'q20.notAcceptingTitle'
                : flavour === 'closed'
                  ? 'q20.closedTitle'
                  : 'q20.outsideHoursTitle',
            )}
          >
            {/* The one line that says what to do next (foundations §9). */}
            {flavour === 'notAccepting' ? (
              <p className="pk-muted">{t('q20.notAcceptingBody')}</p>
            ) : null}

            {/*
              The merchant's own words, set apart in a card because they are not
              our copy: a guest should be able to tell the shop's sentence from
              the software's. Left-aligned inside a centred page because it is
              the one block here that can run to several lines, and centred Thai
              prose at three lines is hard to read.
            */}
            {notice ? (
              <Card className="q20-notice">
                <p lang={notice.lang}>{notice.text}</p>
              </Card>
            ) : null}

            {/*
              A time when the server named one that means today. Otherwise
              `q20.reopensUnknown` — but never on top of the merchant's own notice,
              which is where "back on the 16th" is actually written and which
              "opening time not given" would flatly contradict.
            */}
            {reopen && time(reopen) ? (
              <p className="pk-muted">{t('q20.reopensAt', {time: time(reopen)})}</p>
            ) : flavour !== 'notAccepting' && !notice ? (
              <p className="pk-muted">{t('q20.reopensUnknown')}</p>
            ) : null}
          </TerminalNotice>
        </div>
      </ScreenBody>

      {/* No rule above the footer: a short centred message does not scroll under
          it, and the design draws the plain footer for exactly that case. */}
      <ScreenFooter bordered={false} className="q20-footer">
        {/*
          The guest is mid-meal and their food is already in the kitchen. This
          is the one thing on the page that is genuinely theirs to do, so it is
          the one screen state that spends a solid red — see the header note.
          `q7.viewStatus` is the label for it; there is no `common.*` equivalent
          and inventing a key would put a hard-coded Thai string on a screen
          three languages read. Reported.
        */}
        {flavour === 'notAccepting' && midService ? (
          <ActionLink to={ROUTES.status} variant="primary">
            {t('q7.viewStatus')}
          </ActionLink>
        ) : null}

        {/* 03 §Q20: a menu should still be browsable if the venue allows it.
            Outlined, never filled — looking at a menu you cannot order from is
            not the forward action, whatever else is on the screen. */}
        {menuBrowsable ? (
          <ActionLink to={ROUTES.menu} variant="secondary">
            {t('q20.browseAnyway')}
          </ActionLink>
        ) : null}

        {/*
          A tourist can be sent straight here from a scan, in a language they
          cannot read. Language has to be reachable from every screen
          (foundations §8, 03 §Q2), and a page with nothing else to offer is
          the worst one to be stuck on in the wrong script.

          The SEGMENTED variant, not the link one: there is no header content
          competing for the space, and one tap in their own script beats a
          navigation whose label they cannot read.
        */}
        <LanguageToggle className="q20-lang" />

        {/*
          Present, subordinate, body copy — never a lockup (D-014, foundations §7).
          This is a public brand surface even on the day the shop is shut.
        */}
        <p className="pk-hint">{t('common.attribution')}</p>
      </ScreenFooter>
    </Screen>
  );
}
